<template>
  <div class="stats-all">
    <div class="page-header">
      <button class="btn-back" @click="$router.back()">← Back</button>
      <h1>Statistics - All</h1>
    </div>

    <div v-if="loading" class="loading">Loading...</div>

    <div v-else-if="chartData.length === 0" class="empty">
      No intervention plans found.
    </div>

    <div v-else class="content">
      <!-- Bar chart: avg delta days per student -->
      <div class="chart-card">
        <h2>Average Delta Days per Student</h2>
        <p class="chart-subtitle">Average days overdue (or early) across all IEP goals per student</p>
        <VueApexCharts
          type="line"
          height="350"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>

      <!-- Summary cards -->
      <div class="summary-row">
        <div class="summary-card">
          <div class="summary-label">Overall Avg Delta</div>
          <div class="summary-value">
            {{ overallAvg === null ? 'N/A' : (overallAvg > 0 ? '+' : '') + overallAvg + 'd' }}
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Most Common Delay Reason</div>
          <div class="summary-value">{{ topReason || 'None recorded' }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Total Goals Tracked</div>
          <div class="summary-value">{{ allPlans.length }}</div>
        </div>
      </div>

      <!-- Per-student detail -->
      <div class="details-card">
        <h2>Details by Student</h2>
        <div class="student-row" v-for="s in chartData" :key="s.childID">
          <div class="student-header">
            <span class="student-name student-link" @click="$router.push({ name: 'InterventionPlan', query: { childID: s.childID } })">{{ s.name }}</span>
            <span :class="['delta-badge', s.avgDelta === null ? 'badge-na' : s.avgDelta > 0 ? 'badge-late' : s.avgDelta < 0 ? 'badge-early' : 'badge-ontime']">
              {{ s.avgDelta === null ? 'N/A' : 'avg ' + (s.avgDelta > 0 ? '+' : '') + s.avgDelta + 'd' }}
            </span>
          </div>
          <div class="student-goals">
            <div class="goal-row" v-for="(p, idx) in s.plans" :key="idx" @click="toggleGoal(s.childID + '-' + idx)">
              <div class="goal-header">
                <span class="goal-name">
                  {{ expandedGoals[s.childID + '-' + idx] ? (p.name || p.goal) : truncate(p.name || p.goal, 60) }}
                </span>
                <span :class="['delta-badge', deltaClass(p)]">{{ deltaLabel(p) }}</span>
              </div>
              <div class="goal-meta">
                <div class="meta-item" v-if="p.delay_reason">
                  <span class="meta-label">Reason:</span>
                  <span class="meta-value">{{ p.delay_reason }}</span>
                </div>
                <div class="meta-item" v-if="p.additional_info">
                  <span class="meta-label">Additional info:</span>
                  <span class="meta-value">{{ p.additional_info }}</span>
                </div>
                <div class="meta-item" v-if="!p.delay_reason && !p.additional_info">
                  <span class="meta-value muted">No reason or additional info recorded.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'SpecialistStatisticsAll',
  components: { VueApexCharts },
  data() {
    return {
      allPlans: [],
      childNames: {},
      loading: true,
      expandedGoals: {},
    };
  },
  computed: {
    chartData() {
      const byChild = {};
      for (const p of this.allPlans) {
        const id = p.child_id;
        if (!byChild[id]) byChild[id] = { childID: id, name: this.childNames[id] || id, plans: [] };
        byChild[id].plans.push(p);
      }
      return Object.values(byChild).map(c => {
        const deltas = c.plans.map(p => this.deltaDays(p)).filter(d => d !== null);
        return {
          ...c,
          avgDelta: deltas.length ? Math.round(deltas.reduce((sum, d) => sum + d, 0) / deltas.length) : null,
        };
      });
    },
    sortedPlans() {
      return this.allPlans.slice().sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    },
    filteredPlans() {
      const deltas = this.sortedPlans.map(p => this.deltaDays(p)).filter(d => d !== null);
      if (deltas.length < 2) return this.sortedPlans;
      const mean = deltas.reduce((s, d) => s + d, 0) / deltas.length;
      const std = Math.sqrt(deltas.reduce((s, d) => s + Math.pow(d - mean, 2), 0) / deltas.length);
      return this.sortedPlans.filter(p => {
        const d = this.deltaDays(p);
        return d === null || Math.abs(d - mean) <= 2 * std;
      });
    },
    chartSeries() {
      return [{ name: 'Delta Days', data: this.filteredPlans.map(p => this.deltaDays(p)) }];
    },
    chartOptions() {
      return {
        chart: { type: 'line', toolbar: { show: false } },
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 5 },
        colors: ['#E7A63A'],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.filteredPlans.map(p => this.formatDate(p.due_date)),
          labels: { rotate: -30, style: { fontSize: '11px' } },
        },
        yaxis: { title: { text: 'Days' } },
        tooltip: {
          y: { formatter: val => `${val > 0 ? '+' : ''}${val}d` },
          x: {
            formatter: (val, { dataPointIndex }) => {
              const p = this.filteredPlans[dataPointIndex];
              return p ? (this.childNames[p.child_id] || p.child_id) : val;
            },
          },
        },
      };
    },
    overallAvg() {
      const deltas = this.allPlans.map(p => this.deltaDays(p)).filter(d => d !== null);
      if (!deltas.length) return null;
      return Math.round(deltas.reduce((s, d) => s + d, 0) / deltas.length);
    },
    topReason() {
      const counts = {};
      for (const p of this.allPlans) {
        if (p.delay_reason) counts[p.delay_reason] = (counts[p.delay_reason] || 0) + 1;
      }
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      return top ? top[0] : '';
    },
  },
  async mounted() {
    const base = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || 'http://localhost:8081';
    try {
      const authResp = await fetch(`${base}/api/auth/get-access-cookie-data`, { credentials: 'include' });
      if (!authResp.ok) return;
      const authData = await authResp.json();
      const userID = authData.id;

      const resp = await fetch(`${base}/api/intervention/specialist/${userID}`, { credentials: 'include' });
      if (resp.ok) {
        this.allPlans = await resp.json() || [];
        // fetch names for unique child IDs
        const childIDs = [...new Set(this.allPlans.map(p => p.child_id))];
        await Promise.all(childIDs.map(async id => {
          const r = await fetch(`${base}/api/child/${id}`, { credentials: 'include' });
          if (r.ok) {
            const c = await r.json();
            this.childNames[id] = `${c.first_name} ${c.last_name}`;
          }
        }));
      }
    } catch (e) {
      console.error('StatisticsAll fetch error:', e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    deltaDays(plan) {
      if (!plan.due_date || !plan.actual_completion_date) return null;
      const due = new Date(plan.due_date);
      const end = new Date(plan.actual_completion_date);
      return Math.round((end - due) / 86400000);
    },
    toggleGoal(key) {
      this.$set(this.expandedGoals, key, !this.expandedGoals[key]);
    },
    deltaLabel(plan) {
      const d = this.deltaDays(plan);
      if (d === null) return 'N/A';
      if (d === 0) return 'On time';
      if (d > 0) return `${d}d overdue`;
      return `${Math.abs(d)}d early`;
    },
    deltaClass(plan) {
      const d = this.deltaDays(plan);
      if (d === null) return 'badge-na';
      if (d > 0) return 'badge-late';
      if (d < 0) return 'badge-early';
      return 'badge-ontime';
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
    },
    truncate(str, len) {
      if (!str) return '';
      return str.length > len ? str.slice(0, len) + '…' : str;
    },
  },
};
</script>

<style scoped>
.stats-all { padding: 24px; max-width: 1000px; margin: 50px auto 0; font-family: sans-serif; }
.page-header { margin-bottom: 24px; }
.btn-back { background: none; border: none; color: #aa6d27; cursor: pointer; font-size: 14px; padding: 0; margin-bottom: 8px; display: block; }
.btn-back:hover { text-decoration: underline; }
h1 { font-size: 24px; font-weight: 700; margin: 0 0 4px; color: #1F1F1F; }
.loading, .empty { color: #888; margin-top: 40px; text-align: center; font-size: 15px; }
.chart-card, .details-card { background: #fff; border: 1px solid #f0d9a8; border-radius: 10px; padding: 20px; margin-bottom: 24px; }
h2 { font-size: 16px; font-weight: 600; margin: 0 0 4px; color: #5c300b; }
.chart-subtitle { font-size: 12px; color: #c49040; margin: 0 0 16px; }
.summary-row { display: flex; gap: 16px; margin-bottom: 24px; flex-wrap: wrap; }
.summary-card { flex: 1; min-width: 160px; background: #fff8ee; border: 1px solid #f0d9a8; border-radius: 10px; padding: 16px; }
.summary-label { font-size: 14px; color: #aa6d27; margin-bottom: 6px; font-weight: 600; }
.summary-value { font-size: 16px; font-weight: 700; color: #5c300b; }
.student-row { border-top: 1px solid #f0d9a8; padding: 12px 0; }
.student-row:first-child { border-top: none; }
.student-header { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.student-name { font-weight: 700; font-size: 15px; flex: 1; color: #aa6d27; }
.student-link { cursor: pointer; text-decoration: underline; }
.student-link:hover { color: #E7A63A; }
.delta-badge { font-size: 12px; font-weight: 600; padding: 2px 8px; border-radius: 12px; }
.badge-late  { background: #aa6d27; color: #fff; }
.badge-early { background: #E7A63A; color: #fff; }
.badge-ontime { background: #f0d9a8; color: #aa6d27; }
.badge-na { background: #f3f4f6; color: #6b7280; }
.student-goals { display: flex; flex-direction: column; gap: 4px; padding-left: 8px; }
.goal-row { border-top: 1px solid #f0d9a8; padding: 10px 0; cursor: pointer; }
.goal-row:first-child { border-top: none; }
.goal-row:hover { background: #fff8ee; border-radius: 6px; }
.goal-header { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.goal-name { font-weight: 600; font-size: 14px; flex: 1; color: #5c300b; }
.goal-meta { display: flex; flex-direction: column; gap: 4px; }
.meta-item { display: flex; gap: 6px; font-size: 13px; }
.meta-label { font-weight: 600; color: #aa6d27; white-space: nowrap; }
.meta-value { color: #1F1F1F; }
.muted { color: #c49040; font-style: italic; }
</style>
